import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Shield, Lock, CreditCard } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { TranslatedText } from '@/components/TranslatedText';
import { ModernPricingPage, PricingCardProps } from '@/components/ui/animated-glassy-pricing';

const handleLifetimeCheckout = () => {
  window.location.href = 'https://buy.stripe.com/8x24gz2IFbHHdCNgOBbfO03';
};

const handleMonthlyCheckout = () => {
  window.location.href = 'https://buy.stripe.com/5kQ3cv1EB8vv0Q1dCpbfO05';
};

const pricingPlans: PricingCardProps[] = [
  {
    planName: 'Free Trial',
    description: <>Watch 1 class, <span className="text-primary font-semibold">no card needed</span></>,
    price: '0',
    priceLabel: ' free',
    features: [
      '1 Full Video Lesson',
      'Written Class Content',
      'Platform Preview',
      'No Credit Card Required',
    ],
    buttonText: 'Watch 1 FREE Class',
    buttonVariant: 'secondary',
    onClick: () => { window.location.href = '/apply?plan=free'; },
  },
  {
    planName: 'Monthly Access',
    description: <><span className="text-primary font-semibold">7 days free</span>, then $4.99/mo</>,
    price: '4.99',
    priceLabel: '/mo',
    originalPrice: '9.99',
    discountBadge: '50% OFF — Limited Time',
    features: [
      '7-Day Free Trial Included',
      '500+ Video & Written Lessons',
      '50 Comprehensive Modules',
      'Progressive Module Unlocking',
      'Private Community Access',
      'Cancel Anytime',
    ],
    buttonText: 'Start 7-Day Free Trial',
    buttonVariant: 'secondary',
    onClick: handleMonthlyCheckout,
  },
  {
    planName: 'Lifetime Access',
    description: 'One-time payment, forever yours',
    price: '37',
    priceLabel: ' one-time',
    originalPrice: '74',
    discountBadge: '50% OFF — Limited Time',
    features: [
      '500+ Video & Written Lessons',
      '50 Comprehensive Modules',
      '5 Complete Academies',
      'Private Community Access',
      '7-Day Money-Back Guarantee',
      'Lifetime Updates Included',
    ],
    buttonText: 'Get Lifetime Access',
    isPopular: true,
    buttonVariant: 'primary',
    onClick: handleLifetimeCheckout,
  },
];

export const LightPricingSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  return (
    <section ref={ref} id="pricing">
      <ModernPricingPage
        title={
          <TranslatedText>Choose Your Plan</TranslatedText>
        }
        subtitle={
          <TranslatedText>Start building your faceless empire today.</TranslatedText>
        }
        plans={pricingPlans}
        showAnimatedBackground={true}
      />

      {/* Trust elements below the glassy cards */}
      <div className="bg-background px-4 pb-12 sm:pb-16">
        <div className="max-w-lg mx-auto">
          {/* Payment Security Badges */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="flex items-center justify-center gap-3 flex-wrap mb-4"
          >
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-muted rounded-full">
              <Lock className="w-3.5 h-3.5 text-green-600" />
              <span className="text-xs text-muted-foreground font-medium">
                <TranslatedText>SSL Secure</TranslatedText>
              </span>
            </div>
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-muted rounded-full">
              <CreditCard className="w-3.5 h-3.5 text-primary" />
              <span className="text-xs text-muted-foreground font-medium">
                <TranslatedText>Stripe Payments</TranslatedText>
              </span>
            </div>
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-muted rounded-full">
              <Shield className="w-3.5 h-3.5 text-green-600" />
              <span className="text-xs text-muted-foreground font-medium">
                <TranslatedText>7-Day Guarantee</TranslatedText>
              </span>
            </div>
          </motion.div>

          {/* Card Logos */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="flex items-center justify-center gap-3 text-muted-foreground mb-8"
          >
            <span className="text-xs font-bold tracking-wide">VISA</span>
            <span className="text-xs font-bold tracking-wide">Mastercard</span>
            <span className="text-xs font-bold tracking-wide">AMEX</span>
            <span className="text-xs font-bold tracking-wide">Apple Pay</span>
            <span className="text-xs font-bold tracking-wide">Google Pay</span>
          </motion.div>




          {/* Guarantee */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="p-5 rounded-xl bg-green-50 border border-green-200 flex items-start gap-4"
          >
            <Shield className="w-10 h-10 text-green-600 flex-shrink-0" />
            <div>
              <h4 className="font-bold text-green-800 mb-1">
                <TranslatedText>7-Day Guarantee</TranslatedText>
              </h4>
              <p className="text-green-700 text-sm">
                <TranslatedText>Not satisfied? Get a full refund. No questions asked.</TranslatedText>
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default LightPricingSection;
